import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';

@Component({
	selector: 'app-non-negative-number-input',
	templateUrl: './non-negative-number-input.component.html',
	styleUrls: ['./non-negative-number-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => NonNegativeNumberInputComponent),
			multi: true
		}
	]
})
export class NonNegativeNumberInputComponent implements ControlValueAccessor {
	@Input() appearance: MatFormFieldAppearance = 'outline';
	@Input() labelKey?: string;
	@Input() placeholderKey?: string;
	@Input() suffixKey?: string;
	@Input() maxlength?: number;
	@Input() formatThousands = false;
	@Input() hasError = false;
	@Input() useMaterial = true;
	@Output() readonly inputBlur = new EventEmitter<void>();

	displayValue = '';
	isDisabled = false;

	private value: number | null = null;
	private onChange: (value: number | null) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(private readonly cdr: ChangeDetectorRef) {}

	writeValue(value: string | number | null | undefined): void {
		this.value = this.sanitizeValue(value);
		this.displayValue = this.formatValue(this.value);
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
		this.cdr.markForCheck();
	}

	onInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const nextValue = this.sanitizeValue(input.value);

		this.value = nextValue;
		this.displayValue = this.formatValue(nextValue);
		input.value = this.displayValue;
		this.onChange(nextValue);
	}

	onBlur(): void {
		this.onTouched();
		this.inputBlur.emit();
	}

	onKeydown(event: KeyboardEvent): void {
		if (this.isControlKey(event) || event.ctrlKey || event.metaKey) {
			return;
		}

		if (!/^\d$/.test(event.key)) {
			event.preventDefault();
		}
	}

	onPaste(event: ClipboardEvent): void {
		event.preventDefault();
		const pastedValue = event.clipboardData?.getData('text') ?? '';
		const input = event.target as HTMLInputElement;
		const nextValue = this.sanitizeValue(pastedValue);

		this.value = nextValue;
		this.displayValue = this.formatValue(nextValue);
		input.value = this.displayValue;
		this.onChange(nextValue);
	}

	private sanitizeValue(value: string | number | null | undefined): number | null {
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const digits = String(value)
			.replace(/\D/g, '')
			.slice(0, this.maxlength || undefined);

		if (!digits) {
			return null;
		}

		const parsed = Number(digits);
		return Number.isFinite(parsed) ? parsed : null;
	}

	private formatValue(value: number | null): string {
		if (value === null) {
			return '';
		}

		return this.formatThousands ? new Intl.NumberFormat('en-US').format(value) : String(value);
	}

	private isControlKey(event: KeyboardEvent): boolean {
		return [
			'Backspace',
			'Delete',
			'Tab',
			'Escape',
			'Enter',
			'ArrowLeft',
			'ArrowRight',
			'ArrowUp',
			'ArrowDown',
			'Home',
			'End'
		].includes(event.key);
	}
}
